// src/components/ChatWindow.js
import React, { useState, useEffect, useRef } from 'react';
import { db } from "../../firebase";
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, doc, serverTimestamp, increment } from 'firebase/firestore';
import './Chat.css';

const MessagesSkeleton = () => (
  <div className="messages-container messages-skeleton">
    {Array.from({ length: 5 }).map((_, index) => (
      <div
        key={index}
        className={`message-wrapper ${index % 2 === 0 ? 'them' : 'me'} message-skeleton-wrapper`}
      >
        <div className="message-bubble message-skeleton-bubble">
          <div className="skeleton-line skeleton-line-md" />
          <div className="skeleton-line skeleton-line-sm" />
        </div>
      </div>
    ))}
  </div>
);

const ChatWindow = ({ currentUser, room, onBack }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const currentUserId = currentUser?.uid;

  // Extract other user info for the header
  const otherUserId = room?.participantIds?.find(id => id !== currentUserId);
  const otherUser = room?.participantInfo?.[otherUserId];

  // 1. Listen for real-time messages
  useEffect(() => {
    if (!currentUserId || !room || !room.id) return;

    setLoading(true);

    const messagesRef = collection(db, 'chat_rooms', room.id, 'messages');
    const q = query(messagesRef, orderBy('createdAt', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(msgsData);
      setLoading(false);

      // Auto-scroll to bottom when new messages arrive
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });

    // ✅ NEW: Reset unread count to 0 for the current user when they open the chat
    const resetUnreadCount = async () => {
      try {
        await updateDoc(doc(db, 'chat_rooms', room.id), {
          [`unread_counts.${currentUserId}`]: 0
        });
      } catch (e) {
        console.error("Error clearing unread count", e);
      }
    };

    resetUnreadCount();

    return () => unsubscribe();
  }, [room, currentUserId]);

  // 2. Handle Sending Messages
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!currentUserId) return;
    if (!newMessage.trim()) return;

    const messageText = newMessage.trim();
    setNewMessage(''); // Clear input field instantly for fast UX

    try {
      // A. Add the message to the 'messages' subcollection
      const messagesRef = collection(db, 'chat_rooms', room.id, 'messages');
      await addDoc(messagesRef, {
        text: messageText,
        createdAt: serverTimestamp(),
        userId: currentUserId,
        username: currentUser?.displayName || 'Web User',
        userImage: currentUser?.photoURL || null,
      });

      // ✅ NEW: Increment the OTHER user's unread count
      const otherUserId = room?.participantIds?.find(id => id !== currentUserId);

      // B. Update the parent 'chat_rooms' document so the Sidebar updates
      const roomRef = doc(db, 'chat_rooms', room.id);

      await updateDoc(roomRef, {
        lastMessageText: messageText,
        lastMessageTimestamp: serverTimestamp(),
        hasMessages: true, // Ensure the room is visible in the list
        [`unread_counts.${otherUserId}`]: increment(1)
      });

      // Scroll to bottom after sending
      //messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    return timestamp.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!currentUserId) {
    return (
      <div className="empty-state">
        <div className="empty-state-art">
          <svg width="88" height="88" viewBox="0 0 24 24" fill="none" stroke="#c9ced6" strokeWidth="1.15" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        </div>
        <span className="empty-state-eyebrow">Qinto Secure Chat</span>
        <h2>Please sign in to use chat</h2>
        <p>Log in to access your conversations and send messages.</p>
      </div>
    );
  }

  // --- EMPTY STATE ---
  if (!room) {
    return (
      <div className="empty-state">
        <div className="empty-state-art">
          <svg width="88" height="88" viewBox="0 0 24 24" fill="none" stroke="#c9ced6" strokeWidth="1.15" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        </div>
        <span className="empty-state-eyebrow">Qinto Secure Chat</span>
        <h2>Welcome to Qinto Secure Chat</h2>
        <p>Select a conversation from the left to start messaging.</p>
        <p className="empty-state-note">Your messages are 100% secure and encrypted.</p>
      </div>
    );
  }

  // --- ACTIVE CHAT UI ---
  return (
    <div className="chat-window">
      {/* Header */}
      <div className="chat-window-header">
        <button className="chat-back-btn" onClick={onBack}>←</button>
        <img
          src={otherUser?.image || 'https://ui-avatars.com/api/?name=' + (otherUser?.name || 'U') + '&background=e5e7eb&color=333'}
          alt="User"
        />
        <div>
          <h3>{otherUser?.name || 'Qinto User'}</h3>
          <p>Regarding: {room.adTitle}</p>
        </div>
      </div>

      {/* Messages Area */}
      {loading ? (
        <MessagesSkeleton />
      ) : (
        <div className="messages-container">

          {messages.length === 0 && (
            <div className="chat-window-empty">
              <p>👋 Start conversation about</p>
              <strong>{room.adTitle}</strong>
              <p style={{ marginTop: "10px" }}>
                Say something like "Is this still available?"
              </p>
            </div>
          )}

          {messages.map((msg) => {
            const isMe = msg.userId === currentUserId;
            return (
              <div key={msg.id} className={`message-wrapper ${isMe ? 'me' : 'them'}`}>
                <div className="message-bubble">
                  {msg.text}
                  <div className="message-time">
                    {formatMessageTime(msg.createdAt)}
                  </div>
                </div>
              </div>
            );
          })}
          {/* This empty div ensures we can scroll to the very bottom */}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Input Area */}
      <div className="message-input-container">
        <form onSubmit={handleSendMessage} className="message-form">
          <input
            className="message-input"
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            autoFocus
          />
          <button
            type="submit"
            className="send-button"
            disabled={!newMessage.trim()}
          >
            Send
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatWindow;
